/**
 * What the AI passes the custom tool. Each input wires out to the arguments it fills; an argument
 * dragged onto the empty row becomes a new input. Clicking an input edits it in place.
 */

import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { CustomToolInput } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../../builder/builder-context';
import { inputNameProblem } from '../../custom-tool-draft';
import { inputPort, NEW_INPUT_PORT } from '../ports';
import { CARD, CardHeader, FOCUSED, INTERACTIVE, Row } from './card';
import { InputEditor } from '../../steps/input-editor';
import { Port } from './port';
import { usePortsChanged } from './use-ports-changed';

const InputRow = ({ input, index }: { input: CustomToolInput; index: number }) => {
	const { draft } = useCustomToolBuilder();
	const [open, setOpen] = useState(false);
	const invalid = !!inputNameProblem(draft.inputs, index);
	return (
		<Row>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<button
						type='button'
						aria-label={`Edit input ${input.name}`}
						className={cn(INTERACTIVE, '-mx-1.5 flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-1.5 py-0.5 hover:bg-gray-50')}
					>
						<span className={cn('min-w-0 truncate font-mono text-[11px] leading-4', invalid ? 'text-red-700' : 'text-sky-800')}>{input.name || 'unnamed'}</span>
						<span className='shrink-0 text-[10.5px] leading-4 text-gray-800'>{input.required ? input.type : `${input.type}, optional`}</span>
					</button>
				</PopoverTrigger>
				<PopoverContent align='start' side='right' className='w-80 p-3'>
					<InputEditor index={index} onRemoved={() => setOpen(false)} />
				</PopoverContent>
			</Popover>
			<Port id={inputPort(input.name)} type='source' side='right' tone='ai' />
		</Row>
	);
};

export const InputsNode = () => {
	const { draft, focus } = useCustomToolBuilder();
	// A rename changes the input's port, which its wires look up by id.
	usePortsChanged(draft.inputs.map((input) => input.name).join(','));
	return (
		<div className={cn(CARD, focus?.kind === 'inputs' ? FOCUSED : 'border-gray-300')}>
			<CardHeader icon={<Sparkles className='size-3.5 text-sky-600' aria-hidden />} title='The AI passes' />
			<div className='py-1'>
				{draft.inputs.map((input, index) => (
					// Rows have no id of their own and the name is what gets edited.
					<InputRow key={index} input={input} index={index} />
				))}
				<Row>
					<span className='flex h-5 min-w-0 flex-1 items-center rounded-md border border-dashed border-sky-300 px-1.5 text-[10.5px] leading-4 text-sky-800'>
						<span className='truncate'>Drag an argument here</span>
					</span>
					<Port id={NEW_INPUT_PORT} type='source' side='right' tone='ai-free' />
				</Row>
			</div>
		</div>
	);
};
